import { useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { RootState } from '../../store'

export type LiveMarketTextSelector = (state: RootState) => string

type LiveMarketTextProps = {
    selectText: LiveMarketTextSelector
    selectColor?: LiveMarketTextSelector
    className?: string
    style?: React.CSSProperties
}

/**
 * Renders a stable <span> and updates text/color via DOM only when values change.
 * Parent TableCell / row shells do not re-render on market ticks.
 */
const LiveMarketText = ({ selectText, selectColor, className, style }: LiveMarketTextProps) => {
    const store = useStore<RootState>()
    const ref = useRef<HTMLSpanElement>(null)
    const selectTextRef = useRef(selectText)
    const selectColorRef = useRef(selectColor)
    const lastTextRef = useRef<string | null>(null)
    const lastColorRef = useRef<string | undefined>(undefined)

    selectTextRef.current = selectText
    selectColorRef.current = selectColor

    useEffect(() => {
        const apply = () => {
            const el = ref.current
            if (!el) return
            const state = store.getState()
            const text = selectTextRef.current(state)
            if (lastTextRef.current !== text) {
                el.textContent = text
                lastTextRef.current = text
            }
            if (selectColorRef.current) {
                const color = selectColorRef.current(state)
                if (lastColorRef.current !== color) {
                    el.style.color = color ?? ''
                    lastColorRef.current = color
                }
            } else if (lastColorRef.current !== undefined) {
                el.style.removeProperty('color')
                lastColorRef.current = undefined
            }
        }
        apply()
        return store.subscribe(apply)
    }, [store])

    return <span ref={ref} className={className} style={style} />
}

export default LiveMarketText
